import { UnitClass, UnitType } from '../types/military.js';
/**
 * Unit catalogue, verbatim from the GDD except where noted.
 *
 * PRICING AND SPEED - both are fitted to Tribal Wars 2, the same reference the
 * building costs use, and both are nudged off it on purpose.
 *
 * Costs: every unit with a counterpart there now totals within about 10% of
 * what theirs totals, spread over our four resources instead of their three,
 * with no single line copied. The units that have no counterpart (the militia
 * archer, crossbowman, scout, knight, cannon and the three hulls) are priced
 * to sit where they always sat against the ones that do.
 *
 * Speed is minutes per tile, so lower is faster. Ours were the *first* Tribal
 * Wars' figures; the sequel shaved every unit by roughly a fifth, and these
 * are that fifth, rounded our own way. The relative order is untouched: the
 * scout is still the fastest thing on the board, siege still the slowest, and
 * the knight still keeps pace with light cavalry.
 *
 * SIEGE TRAINING BUILDING - the GDD gates Rams and Catapults behind
 * "Smithy L8: Siege Workshop" but never defines a Workshop building (no cost,
 * no level curve), even though the art pack includes one. Siege is therefore
 * trained at the Smithy itself. When a Workshop is specified, change
 * `trainedAt` here and nothing else needs to move.
 */
export const UNITS = {
    spearman: {
        type: UnitType.SPEARMAN,
        name: 'Spearman',
        role: 'Anti-cavalry / defensive infantry',
        unitClass: UnitClass.INFANTRY,
        attack: 10,
        defInfantry: 15,
        defCavalry: 45,
        defArchers: 20,
        speed: 15,
        carryCapacity: 25,
        population: 1,
        cost: { wood: 45, clay: 30, iron: 25, grain: 10 },
        trainingTimeSeconds: 360,
        trainedAt: 'barracks',
        requires: { barracks: 1 },
    },
    swordsman: {
        type: UnitType.SWORDSMAN,
        name: 'Swordsman',
        role: 'Anti-infantry / defensive infantry',
        unitClass: UnitClass.INFANTRY,
        attack: 25,
        defInfantry: 50,
        defCavalry: 25,
        defArchers: 40,
        speed: 19,
        carryCapacity: 15,
        population: 1,
        /*
         * Iron-heavy on purpose: a sword is the one early weapon a smith makes.
         *
         * It cost 40/50/30 like everything else, and with the rest of tier one
         * leaning on wood, iron had no buyer until crossbows and cavalry - a
         * mine you build in week one for a payoff in week three. The swordsman
         * is the classic early iron sink (Tribal Wars prices its swordsman at
         * 30/30/70 for the same reason), and pricing him that way gives the
         * iron mine a customer from the first barracks upgrade.
         */
        cost: { wood: 35, clay: 30, iron: 65, grain: 15 },
        trainingTimeSeconds: 480,
        trainedAt: 'barracks',
        requires: { barracks: 3 },
    },
    axeman: {
        type: UnitType.AXEMAN,
        name: 'Axeman',
        role: 'Primary offensive infantry',
        unitClass: UnitClass.INFANTRY,
        attack: 45,
        defInfantry: 10,
        defCavalry: 5,
        defArchers: 10,
        speed: 15,
        carryCapacity: 10,
        population: 1,
        cost: { wood: 55, clay: 35, iron: 45, grain: 15 },
        trainingTimeSeconds: 600,
        trainedAt: 'barracks',
        requires: { barracks: 5 },
    },
    /**
     * The village levy: townsfolk handed a bow.
     *
     * Exists to fill the gap before the Archer, which needs Barracks 8. Until
     * then a new player has no ranged defence at all and no answer to a spear
     * wall, so the early game had one viable defensive shape. Cheap, weak, and
     * available almost immediately.
     */
    militia_archer: {
        type: UnitType.MILITIA_ARCHER,
        name: 'Militia Archer',
        role: 'Early ranged defence',
        unitClass: UnitClass.ARCHER,
        attack: 8,
        defInfantry: 25,
        defCavalry: 20,
        defArchers: 12,
        speed: 16,
        carryCapacity: 5,
        population: 1,
        cost: { wood: 45, clay: 18, iron: 7, grain: 10 },
        trainingTimeSeconds: 300,
        trainedAt: 'barracks',
        requires: { barracks: 2 },
    },
    archer: {
        type: UnitType.ARCHER,
        name: 'Archer',
        role: 'Ranged defence / support',
        unitClass: UnitClass.ARCHER,
        attack: 15,
        defInfantry: 50,
        defCavalry: 40,
        /*
         * Was 5, a tenth of its anti-infantry value. That is the widest single
         * spread in the game and it made archer-versus-archer a blowout: an
         * attacking archer stack broke a defending one for barely half its cost,
         * where every other matchup sits near or above parity. 15 keeps archers
         * clearly the wrong answer to archers without making the counter absolute.
         */
        defArchers: 15,
        speed: 15,
        carryCapacity: 10,
        population: 1,
        cost: { wood: 75, clay: 30, iron: 55, grain: 15 },
        trainingTimeSeconds: 720,
        trainedAt: 'barracks',
        requires: { barracks: 8 },
    },
    /**
     * Armour-piercing ranged offence, gated behind the Smithy.
     *
     * Named in the design brief as a Smithy research unlock. It fills a hole in
     * the roster rather than upgrading the Archer: before it, ranged offence did
     * not exist. The Archer costs 135 for 15 attack, so it can only ever sit at
     * home, and every offensive option was infantry or horse.
     *
     * Its counter row is the point. Nothing else cheap threatens plate, so the
     * 1.6 into Heavy Cavalry and 1.7 into Knights are what buy it a place. A
     * first pass gave it 40 attack, which made the matrix bonus multiply a base
     * too small to matter: it came out a generalist with no bad matchup and no
     * good one. Offence had to come up for the counter to mean anything.
     */
    crossbowman: {
        type: UnitType.CROSSBOWMAN,
        name: 'Crossbowman',
        role: 'Armour-piercing ranged offence',
        unitClass: UnitClass.ARCHER,
        attack: 62,
        defInfantry: 48,
        defCavalry: 55,
        defArchers: 22,
        speed: 17,
        carryCapacity: 10,
        population: 2,
        cost: { wood: 145, clay: 55, iron: 120, grain: 30 },
        trainingTimeSeconds: 900,
        trainedAt: 'barracks',
        requires: { barracks: 10, smithy: 15 },
    },
    scout: {
        type: UnitType.SCOUT,
        name: 'Scout',
        role: 'Reconnaissance',
        unitClass: UnitClass.CAVALRY,
        attack: 0,
        defInfantry: 2,
        defCavalry: 1,
        defArchers: 2,
        speed: 8,
        carryCapacity: 0,
        population: 1,
        cost: { wood: 35, clay: 30, iron: 25, grain: 10 },
        trainingTimeSeconds: 600,
        trainedAt: 'stable',
        requires: { stable: 1 },
    },
    light_cavalry: {
        type: UnitType.LIGHT_CAVALRY,
        name: 'Light Cavalry',
        role: 'Fast offence / raiding',
        unitClass: UnitClass.CAVALRY,
        attack: 130,
        defInfantry: 30,
        defCavalry: 40,
        defArchers: 30,
        speed: 9,
        carryCapacity: 80,
        population: 2,
        cost: { wood: 115, clay: 95, iron: 235, grain: 45 },
        trainingTimeSeconds: 1440,
        trainedAt: 'stable',
        requires: { stable: 3 },
    },
    heavy_cavalry: {
        type: UnitType.HEAVY_CAVALRY,
        name: 'Heavy Cavalry',
        role: 'Elite general offence / defence',
        unitClass: UnitClass.CAVALRY,
        attack: 150,
        defInfantry: 200,
        defCavalry: 160,
        defArchers: 180,
        speed: 10,
        carryCapacity: 50,
        population: 3,
        cost: { wood: 185, clay: 140, iron: 560, grain: 70 },
        trainingTimeSeconds: 2400,
        trainedAt: 'stable',
        requires: { stable: 8, smithy: 5 },
    },
    ram: {
        type: UnitType.RAM,
        name: 'Battering Ram',
        role: 'Siege - wall destruction',
        unitClass: UnitClass.SIEGE,
        attack: 2,
        defInfantry: 20,
        defCavalry: 50,
        defArchers: 20,
        speed: 25,
        carryCapacity: 0,
        population: 4,
        cost: { wood: 290, clay: 195, iron: 210, grain: 50 },
        trainingTimeSeconds: 1920,
        trainedAt: 'smithy',
        requires: { smithy: 8 },
    },
    catapult: {
        type: UnitType.CATAPULT,
        name: 'Catapult',
        role: 'Siege - building destruction',
        unitClass: UnitClass.SIEGE,
        attack: 100,
        defInfantry: 100,
        defCavalry: 50,
        defArchers: 100,
        speed: 25,
        carryCapacity: 0,
        population: 5,
        cost: { wood: 340, clay: 380, iron: 130, grain: 90 },
        trainingTimeSeconds: 1800,
        trainedAt: 'smithy',
        requires: { smithy: 10 },
    },
    bailiff: {
        type: UnitType.BAILIFF,
        name: 'Bailiff',
        role: 'Village subjugation',
        unitClass: UnitClass.SPECIAL,
        attack: 30,
        defInfantry: 100,
        defCavalry: 100,
        defArchers: 100,
        speed: 34,
        carryCapacity: 0,
        // Twenty-five each, so a conquest party is a hundred population standing
        // idle in your farm. That weight is the real cost of taking land, and it
        // is what stops a village changing hands on a spare afternoon.
        population: 25,
        /*
         * The single largest correction in the catalogue: this was 2,800.
         *
         * Four Bailiffs take a village, so a conquest used to cost 11,200
         * resources, which a finished holding earns back in about an hour. The
         * building it answers to over in Tribal Wars 2 costs 140,000 apiece, and
         * four of those are two and a half days of a finished village's output.
         * That is the difference between taking land on a spare afternoon and
         * mounting a campaign for it, and the whole late game hangs off which one
         * it is.
         *
         * At 146,000 a head, four of them are a little under 584,000, which is
         * about fifty-four hours of our own maxed output. The clay line is the
         * binding one: 48,000 of it needs a Warehouse at 20 before you can even
         * hold the price.
         */
        cost: { wood: 38_000, clay: 48_000, iron: 48_000, grain: 12_000 },
        trainingTimeSeconds: 10800,
        trainedAt: 'manor_house',
        requires: { manor_house: 15 },
    },
    knight: {
        type: UnitType.KNIGHT,
        name: 'Knight',
        role: 'Elite heavy infantry',
        /*
         * INFANTRY, not cavalry: the Knight fights dismounted, in full plate, with
         * a greatsword. The class decides which of a defender's three defence
         * stats applies, so while this was CAVALRY a spear wall met him with its
         * anti-cavalry value (45 on a Spearman) instead of its anti-infantry one
         * (15). A hedge of spears stops a horse; it does not stop a man on foot.
         */
        unitClass: UnitClass.INFANTRY,
        /*
         * Tuned to be population-efficient rather than resource-efficient, which
         * is what makes it worth its gate. At the old numbers Heavy Cavalry beat
         * it on value per resource AND per population, leaving the most expensive
         * unlock in the game with no dimension it actually won: only speed, carry
         * and its matrix row. Now the two split cleanly. Per 100 resources Heavy
         * Cavalry is still ahead; per population point the Knight is, and
         * population is the binding constraint late, because Farm levels are the
         * steepest curve a village climbs.
         */
        attack: 220,
        defInfantry: 280,
        defCavalry: 230,
        defArchers: 250,
        speed: 9,
        carryCapacity: 100,
        population: 4,
        cost: { wood: 355, clay: 265, iron: 650, grain: 150 },
        trainingTimeSeconds: 1800,
        /*
         * Barracks, not Stable. The Knight was cavalry once and kept its Stable
         * gate when it was reclassified as infantry, which left the game training
         * a man who fights on foot in a paddock. The Stable requirement moved to
         * the Barracks at the same depth, so the unlock is no easier, just
         * coherent: plate and a greatsword are drilled in the yard.
         */
        trainedAt: 'barracks',
        requires: { barracks: 20, smithy: 10, manor_house: 20 },
    },
    cannon: {
        type: UnitType.CANNON,
        name: 'Cannon',
        role: 'Heavy gun - siege and coastal defence',
        /*
         * The Smithy 20 unlock the design brief always named but never defined.
         *
         * Deliberately slow and expensive enough that it is a decision rather than
         * a staple: it out-damages a catapult and is the only thing on land that
         * can hurt a fleet, but at eight population and thirty minutes a gun it
         * cannot be the backbone of an army.
         */
        unitClass: UnitClass.SIEGE,
        attack: 210,
        defInfantry: 140,
        defCavalry: 70,
        defArchers: 140,
        speed: 30,
        carryCapacity: 0,
        population: 8,
        cost: { wood: 560, clay: 420, iron: 900, grain: 120 },
        trainingTimeSeconds: 3600,
        trainedAt: 'smithy',
        requires: { smithy: 20 },
    },
    cog: {
        type: UnitType.COG,
        name: 'Cog',
        role: 'Transport - carries an army over water',
        /*
         * The workhorse. Without hulls no army leaves the mainland, so this is
         * cheap and carries a lot, and is nearly helpless in a fight: a fleet of
         * loaded cogs with no escort is an invitation.
         */
        unitClass: UnitClass.NAVAL,
        attack: 20,
        defInfantry: 120,
        defCavalry: 120,
        defArchers: 120,
        speed: 19,
        carryCapacity: 200,
        population: 6,
        transport: 20,
        cost: { wood: 640, clay: 170, iron: 100, grain: 60 },
        trainingTimeSeconds: 2700,
        trainedAt: 'dockyard',
        requires: { dockyard: 1 },
    },
    longship: {
        type: UnitType.LONGSHIP,
        name: 'Longship',
        role: 'Raider - fast, carries a little, fights',
        /*
         * The middle option, and the one that makes coastal raiding viable without
         * committing a fleet: fast enough to be somewhere else by the time a
         * response is mustered, and able to carry the handful of men a raid needs.
         */
        unitClass: UnitClass.NAVAL,
        attack: 90,
        defInfantry: 100,
        defCavalry: 100,
        defArchers: 100,
        speed: 15,
        carryCapacity: 120,
        population: 5,
        transport: 8,
        cost: { wood: 570, clay: 135, iron: 200, grain: 60 },
        trainingTimeSeconds: 3600,
        trainedAt: 'dockyard',
        requires: { dockyard: 5 },
    },
    war_galley: {
        type: UnitType.WAR_GALLEY,
        name: 'War Galley',
        role: 'Warship - sinks hulls, carries nothing',
        /*
         * Carries no cargo at all, which is the point: a galley exists to sink the
         * transports behind it. Drowning an army in its cogs is far cheaper than
         * beating that army on the beach.
         */
        unitClass: UnitClass.NAVAL,
        attack: 190,
        defInfantry: 220,
        defCavalry: 220,
        defArchers: 220,
        speed: 24,
        carryCapacity: 60,
        population: 10,
        transport: 0,
        cost: { wood: 1150, clay: 330, iron: 650, grain: 160 },
        trainingTimeSeconds: 7200,
        trainedAt: 'dockyard',
        requires: { dockyard: 12, smithy: 5 },
    },
};
export function getUnit(type) {
    const def = UNITS[type];
    if (!def)
        throw new Error(`Unknown unit type: ${type}`);
    return def;
}
/** The defence stat that applies when `attackerClass` comes at you. */
export function defenceStatFor(def, attackerClass) {
    switch (attackerClass) {
        case UnitClass.CAVALRY:
            return def.defCavalry;
        case UnitClass.ARCHER:
            return def.defArchers;
        // Siege engines and Bailiffs advance on foot and are met by the
        // anti-infantry line.
        case UnitClass.INFANTRY:
        case UnitClass.SIEGE:
        case UnitClass.SPECIAL:
        // A hull carries one defence value written into all three stats, so it
        // does not matter which line asks.
        case UnitClass.NAVAL:
        default:
            return def.defInfantry;
    }
}
export const UNITS_BY_BUILDING = {
    barracks: [
        UnitType.SPEARMAN,
        UnitType.SWORDSMAN,
        UnitType.AXEMAN,
        UnitType.MILITIA_ARCHER,
        UnitType.ARCHER,
        UnitType.CROSSBOWMAN,
        UnitType.KNIGHT,
    ],
    stable: [UnitType.SCOUT, UnitType.LIGHT_CAVALRY, UnitType.HEAVY_CAVALRY],
    smithy: [UnitType.RAM, UnitType.CATAPULT, UnitType.CANNON],
    manor_house: [UnitType.BAILIFF],
    dockyard: [UnitType.COG, UnitType.LONGSHIP, UnitType.WAR_GALLEY],
};
//# sourceMappingURL=units.js.map